import { Controller } from "@hotwired/stimulus"

// Technical industries that should show tech stack field
const TECHNICAL_INDUSTRIES = [
  "Technology", "Information Technology & Services", "Computer Software", "Computer Hardware", "Internet", "Telecommunications", "Computer & Network Security", "Computer Games", "Semiconductors", "Electronics", "Aerospace", "Defense & Space", "Biotechnology", "Nanotechnology", "Engineering", "Cloud Computing", "Web Development", "Mobile Development", "Data Science", "Machine Learning", "Artificial Intelligence", "Blockchain", "Game Development", "DevOps", "Software Development", "IT Services", "Cybersecurity"
];

export default class extends Controller {
  static targets = ["industry", "skills", "techStack", "skillsList", "interests", "interestsList"]

  connect() {
    const industry = this.industryTarget.value
    if (industry) {
      this.loadIndustryDependentFields()
    } else {
      this.clearSkills()
      this.clearInterests()
    }
  }

  async updateSkills() {
    const industry = this.industryTarget.value

    if (!industry) {
      this.clearSkills()
      this.clearInterests()
      return
    }

    await this.loadIndustryDependentFields()
  }

  async loadIndustryDependentFields() {
    const industry = this.industryTarget.value
    if (!industry) {
      this.clearSkills()
      this.clearInterests()
      return
    }

    const [skillsResponse, interestsResponse] = await Promise.all([
      fetch(`/api/industry_skills/skills_for_industry?industry=${encodeURIComponent(industry)}`, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      }),
      fetch(`/api/industry_skills/interests_for_industry?industry=${encodeURIComponent(industry)}`, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      })
    ])

    const skillsData = await skillsResponse.json()
    const interestsData = await interestsResponse.json()

    if (skillsResponse.ok) {
      this.updateSkillsOptions(skillsData.skills || [])
    } else {
      if (skillsResponse.status === 401) {
        window.location.href = '/sessions/new'
        return
      }
      this.clearSkills()
    }

    if (interestsResponse.ok) {
      this.updateInterestsOptions(interestsData.interests || [])
    } else {
      if (interestsResponse.status === 401) {
        window.location.href = '/sessions/new'
        return
      }
      this.clearInterests()
    }

    if (this.hasTechStackTarget) {
      this.techStackTarget.style.display = TECHNICAL_INDUSTRIES.includes(industry) ? "" : "none"
    }
  }

  clearSkills() {
    const skillsSelect = this.skillsTarget
    skillsSelect.innerHTML = ''
    skillsSelect.disabled = true
    this.updateSkillsList()
  }

  clearInterests() {
    if (!this.hasInterestsTarget) return
    const interestsSelect = this.interestsTarget
    interestsSelect.innerHTML = ''
    interestsSelect.disabled = true
    this.updateInterestsList()
  }

  updateSkillsOptions(skills) {
    const skillsSelect = this.skillsTarget
    const selected = Array.from(skillsSelect.selectedOptions).map(opt => opt.value)

    skillsSelect.innerHTML = ''
    skillsSelect.disabled = false

    skills.forEach(skill => {
      const option = document.createElement("option")
      option.value = skill
      option.text = skill
      if (selected.includes(skill)) option.selected = true
      skillsSelect.appendChild(option)
    })

    this.updateSkillsList()
  }

  updateInterestsOptions(interests) {
    if (!this.hasInterestsTarget) return
    const interestsSelect = this.interestsTarget
    const selected = Array.from(interestsSelect.selectedOptions).map(opt => opt.value)

    interestsSelect.innerHTML = ''
    interestsSelect.disabled = false

    interests.forEach(interest => {
      const option = document.createElement("option")
      option.value = interest
      option.text = interest
      if (selected.includes(interest)) option.selected = true
      interestsSelect.appendChild(option)
    })

    this.updateInterestsList()
  }

  updateInterestsList() {
    if (!this.hasInterestsListTarget) return
    const selected = Array.from(this.interestsTarget.selectedOptions).map(opt => opt.value)
    this.interestsListTarget.innerHTML = ''
    selected.forEach(interest => {
      const pill = document.createElement('span')
      pill.className = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-200 text-dk-teal mr-1.5 mb-2'
      pill.textContent = interest
      const removeBtn = document.createElement('button')
      removeBtn.type = 'button'
      removeBtn.className = 'text-white/80 hover:text-white focus:outline-none text-xs'
      removeBtn.setAttribute('aria-label', `Remove ${interest}`)
      removeBtn.innerHTML = '&times;'
      removeBtn.addEventListener('click', () => {
        const values = Array.from(this.interestsTarget.selectedOptions).map(opt => opt.value)
        const newValues = values.filter(v => v !== interest)
        Array.from(this.interestsTarget.options).forEach(option => {
          option.selected = newValues.includes(option.value)
        })
        this.updateInterestsList()
      })
      pill.appendChild(removeBtn)
      this.interestsListTarget.appendChild(pill)
    })
  }

  updateSkillsList() {
    if (!this.hasSkillsListTarget) return
    const selected = Array.from(this.skillsTarget.selectedOptions).map(opt => opt.value)
    this.skillsListTarget.innerHTML = ''
    selected.forEach(skill => {
      const pill = document.createElement('span')
      pill.className = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-200 text-dk-teal mr-1.5 mb-2'
      pill.textContent = skill
      const removeBtn = document.createElement('button')
      removeBtn.type = 'button'
      removeBtn.className = 'text-white/80 hover:text-white focus:outline-none text-xs'
      removeBtn.setAttribute('aria-label', `Remove ${skill}`)
      removeBtn.innerHTML = '&times;'
      removeBtn.addEventListener('click', () => {
        const values = Array.from(this.skillsTarget.selectedOptions).map(opt => opt.value)
        const newValues = values.filter(v => v !== skill)
        // Update the select element
        Array.from(this.skillsTarget.options).forEach(option => {
          option.selected = newValues.includes(option.value)
        })
        this.updateSkillsList()
      })
      pill.appendChild(removeBtn)
      this.skillsListTarget.appendChild(pill)
    })
  }
}